/**
 * @file Appointment hooks — list, detail, and all lifecycle actions.
 *
 * The most complex hook file — appointments have many actions beyond CRUD:
 * confirm, complete, cancel, reschedule, add notes.
 *
 * Two hooks:
 *   - {@link useAppointments} — list + all actions (used by list page)
 *   - {@link useAppointmentDetail} — single appointment (used by detail page)
 *
 * Key pattern: After each action (confirm, cancel, etc.), we update the
 * local state immediately using setAppointments with .map() — this replaces
 * just the changed appointment in the array without re-fetching the whole list.
 *
 * @module hooks/useAppointments
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { appointmentService } from "../services";
import { APPOINTMENT_STRINGS } from "../constants/strings";

/**
 * Hook for appointment list + lifecycle actions.
 * @param {Object} [options]
 * @param {boolean} [options.autoFetch=true] - Fetch on mount.
 * @param {Object} [options.filters={}] - Default query params (e.g. { dateFrom, status }).
 * @returns {{ appointments: Object[], isLoading: boolean, error: Error|null, refresh: Function, createAppointment: Function, completeAppointment: Function, cancelAppointment: Function, rescheduleAppointment: Function, saveNotes: Function }}
 */
export function useAppointments(options = {}) {
  const { autoFetch = true, filters = {} } = options;
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all appointments, optionally with filters like ?status=PENDIENTE
  const fetchAppointments = useCallback(async (params) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await appointmentService.getAll(params || filters);
      setAppointments(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create a new appointment (doesn't add to local list — user navigates away)
  const createAppointment = async (data) => {
    const response = await appointmentService.create(data);
    toast.success(APPOINTMENT_STRINGS.CREATED);
    return response.data;
  };

  // AGENDADA/CONFIRMADA → COMPLETADA (professional only)
  const completeAppointment = async (id) => {
    const response = await appointmentService.complete(id);
    setAppointments((prev) =>
      prev.map((a) => (a.id === id ? response.data : a))
    );
    toast.success(APPOINTMENT_STRINGS.COMPLETED);
    return response.data;
  };

  // Any status → CANCELADA (both roles can cancel)
  const cancelAppointment = async (id, data = {}) => {
    const response = await appointmentService.cancel(id, data);
    setAppointments((prev) =>
      prev.map((a) => (a.id === id ? response.data : a))
    );
    toast.success(APPOINTMENT_STRINGS.CANCELLED);
    return response.data;
  };

  // Change date/time, resets status to PENDIENTE
  const rescheduleAppointment = async (id, data) => {
    const response = await appointmentService.reschedule(id, data);
    setAppointments((prev) =>
      prev.map((a) => (a.id === id ? response.data : a))
    );
    toast.success(APPOINTMENT_STRINGS.RESCHEDULED);
    return response.data;
  };

  // Add/update clinical notes (professional only)
  const saveNotes = async (id, notes) => {
    const response = await appointmentService.addNotes(id, notes);
    setAppointments((prev) =>
      prev.map((a) => (a.id === id ? response.data : a))
    );
    toast.success(APPOINTMENT_STRINGS.NOTES_SAVED);
    return response.data;
  };

  useEffect(() => {
    if (autoFetch) fetchAppointments();
  }, [autoFetch, fetchAppointments]);

  return {
    appointments,
    isLoading,
    error,
    refresh: fetchAppointments,
    createAppointment,
    completeAppointment,
    cancelAppointment,
    rescheduleAppointment,
    saveNotes,
  };
}

// Hook for viewing a single appointment's details
// Also exposes setAppointment so the detail page can update local state
// after performing actions (confirm, cancel, etc.)
export function useAppointmentDetail(id) {
  const [appointment, setAppointment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAppointment = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      setError(null);
      const response = await appointmentService.getById(id);
      setAppointment(response.data);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAppointment();
  }, [fetchAppointment]);

  return { appointment, setAppointment, isLoading, error, refresh: fetchAppointment };
}
